import { useState, useEffect, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useStatusBar } from "@/hooks/useStatusBar";

interface WelcomeOnboardingProps {
  onDismiss: () => void;
}

const SOFT_VIOLET = "#C8C8F4";
const ELEC_LAV = "#9090E8";
const PERIWINKLE = "#5050C8";
const CHARCOAL = "#1C1A3E";

const KinMark = ({ size = 80, radius = 22 }: { size?: number; radius?: number }) => (
  <div
    className="relative flex items-center justify-center"
    style={{
      width: size,
      height: size,
      borderRadius: radius,
      background: `linear-gradient(145deg, ${CHARCOAL}, #2C2A5A)`,
      border: "2px solid rgba(80,80,200,0.27)",
    }}
  >
    <span
      className="font-bold leading-none"
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: size * 0.55,
        color: "#FFFFFF",
        marginTop: 2,
      }}
    >
      K
    </span>
    <span
      className="absolute"
      style={{
        top: "14%",
        right: "14%",
        fontSize: size * 0.16,
        color: ELEC_LAV,
        opacity: 0.95,
      }}
    >
      ✦
    </span>
  </div>
);

const KAvatar = ({ size = 22 }: { size?: number }) => (
  <div
    className="flex items-center justify-center flex-shrink-0"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`,
    }}
  >
    <span
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: size * 0.5,
        color: "#FFFFFF",
        lineHeight: 1,
      }}
    >
      K
    </span>
  </div>
);

interface StepShellProps {
  headlinePre: string;
  headlineAccent: string;
  subline: string;
  body?: string;
  children?: ReactNode;
}

const StepShell = ({ headlinePre, headlineAccent, subline, body, children }: StepShellProps) => (
  <div className="flex flex-col items-center text-center w-full">
    <h1
      className="welcome-fade"
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 28,
        color: "#FFFFFF",
        lineHeight: 1.2,
        marginBottom: 8,
        animationDelay: "60ms",
      }}
    >
      {headlinePre}{" "}
      <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", color: ELEC_LAV }}>
        {headlineAccent}
      </span>
    </h1>
    <p
      className="welcome-fade"
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 15,
        color: SOFT_VIOLET,
        lineHeight: 1.5,
        whiteSpace: "pre-line",
        marginBottom: body ? 8 : 18,
        animationDelay: "140ms",
      }}
    >
      {subline}
    </p>
    {body && (
      <p
        className="welcome-fade"
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 14,
          color: SOFT_VIOLET,
          opacity: 0.6,
          lineHeight: 1.5,
          marginBottom: 18,
          animationDelay: "200ms",
        }}
      >
        {body}
      </p>
    )}
    <div className="w-full">{children}</div>
  </div>
);

// ─── Step 1 ───
const Step1 = () => {
  const tags = [
    "🍽️ Meal Plans",
    "📅 Weekly Planner",
    "🔨 Budget Tracker",
    "📧 Work Emails",
    "🌱 Garden Plans",
    "🛒 Grocery Cart",
  ];
  return (
    <StepShell
      headlinePre="Hey mama."
      headlineAccent="I'm Kin."
      subline="Your AI bestie for the whole messy, beautiful life."
      body="Not techy. Not perfect. Just built for moms doing it all. Let me show you around."
    >
      <div className="flex flex-col items-center">
        <div className="welcome-fade welcome-float" style={{ animationDelay: "260ms", marginBottom: 18 }}>
          <KinMark />
        </div>
        <div className="flex flex-wrap justify-center gap-1.5" style={{ maxWidth: 320 }}>
          {tags.map((t, i) => (
            <span
              key={t}
              className="welcome-fade"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 20,
                padding: "6px 12px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                fontWeight: 500,
                color: SOFT_VIOLET,
                animationDelay: `${340 + i * 60}ms`,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </StepShell>
  );
};

// ─── Step 2 ───
const Step2 = () => {
  const cards = [
    { emoji: "🏢", label: "Corporate Mom", chips: ["Prep for a big meeting", "Write a work email"] },
    { emoji: "🌱", label: "Homestead / DIY Mom", chips: ["Garden planning", "Backyard chicken help"] },
    { emoji: "✨", label: "Creative / Side Hustle Mom", chips: ["Content for my brand", "Pick a business idea"] },
  ];
  return (
    <StepShell
      headlinePre="Built around"
      headlineAccent="your life."
      subline="Kin learns what kind of mom you are, then shows you the right shortcuts."
    >
      <div className="flex flex-col gap-2">
        {cards.map((c, i) => (
          <div
            key={c.label}
            className="welcome-fade"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              padding: "10px 14px",
              animationDelay: `${260 + i * 100}ms`,
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span style={{ fontSize: 18 }}>{c.emoji}</span>
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#FFFFFF",
                }}
              >
                {c.label}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {c.chips.map((chip) => (
                <span
                  key={chip}
                  style={{
                    background: "rgba(80,80,200,0.09)",
                    border: "1px solid rgba(80,80,200,0.2)",
                    borderRadius: 10,
                    padding: "4px 9px",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 11.5,
                    color: ELEC_LAV,
                  }}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p
        className="welcome-fade text-center"
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontStyle: "italic",
          fontSize: 11.5,
          color: SOFT_VIOLET,
          opacity: 0.53,
          marginTop: 12,
          animationDelay: "600ms",
        }}
      >
        Pick up to 3 from 6 types. Change anytime in settings.
      </p>
    </StepShell>
  );
};

// ─── Step 3 ───
const Step3 = () => {
  const steps = [
    { icon: "📸", label: "Snap your fridge or pantry", highlight: false },
    { icon: "🤖", label: "Kin sees what you have, plans meals around it", highlight: false },
    { icon: "🛒", label: "Grocery list auto-generates for what's missing", highlight: false },
    { icon: "🏪", label: "Real store prices by your zip code", highlight: false },
    { icon: "✓", label: "Add it all to your store cart. One tap.", highlight: true },
  ];
  return (
    <StepShell
      headlinePre="Snap your fridge."
      headlineAccent="Get dinner."
      subline="From photo to store cart in under 2 minutes. For real."
    >
      <div className="flex flex-col items-stretch">
        {steps.map((s, i) => (
          <div key={i}>
            <div
              className="welcome-fade flex items-center gap-3"
              style={{
                animationName: "welcomeFadeRight",
                animationDelay: `${260 + i * 100}ms`,
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: s.highlight
                    ? `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`
                    : "rgba(255,255,255,0.07)",
                  boxShadow: s.highlight ? "0 6px 22px rgba(80,80,200,0.4)" : "none",
                  fontSize: 18,
                  color: s.highlight ? "#FFFFFF" : undefined,
                }}
              >
                {s.icon}
              </div>
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: s.highlight ? "#FFFFFF" : SOFT_VIOLET,
                  fontWeight: s.highlight ? 600 : 400,
                  textAlign: "left",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex" style={{ paddingLeft: 19 }}>
                <div
                  style={{
                    width: 1.5,
                    height: 16,
                    background: `linear-gradient(to bottom, rgba(200,200,244,0.2), transparent)`,
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </StepShell>
  );
};

// ─── Step 4 ───
const Step4 = () => {
  const badgeStyle = {
    fontFamily: "'Plus Jakarta Sans', sans-serif" as const,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.5px",
    padding: "2px 7px",
    borderRadius: 6,
    background: PERIWINKLE,
    color: "#FFFFFF",
    display: "inline-block",
  };
  const sectionLabel = {
    color: PERIWINKLE,
    fontFamily: "'Plus Jakarta Sans', sans-serif" as const,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "1px",
    marginTop: 8,
    marginBottom: 6,
  };
  const tabCardStyle = {
    flex: 1,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 14,
    padding: "10px 12px",
    textAlign: "left" as const,
    minWidth: 0,
  };
  const todoItems = ["Pack snacks for game", "Sign permission slip"];
  const scheduleItems = [
    { time: "9:00 AM", title: "Team meeting" },
    { time: "3:30 PM", title: "Soccer practice" },
  ];
  const myKinRows = [
    { icon: "🛒", label: "Grocery Lists" },
    { icon: "🍽️", label: "Meal Plans & Recipes" },
    { icon: "📝", label: "Notes & To-Dos" },
    { icon: "💰", label: "Budgets" },
  ];
  return (
    <StepShell
      headlinePre="Your whole life."
      headlineAccent="One place."
      subline="Today is what's happening. My Kin is what you've saved. Both right where you need them."
    >
      <div className="flex flex-row gap-2 items-stretch">
        {/* TODAY tab */}
        <div className="welcome-fade" style={{ ...tabCardStyle, animationDelay: "260ms" }}>
          <span style={badgeStyle}>TODAY</span>
          <div style={sectionLabel}>SCHEDULE</div>
          {scheduleItems.map((e) => (
            <div key={e.title} style={{ marginBottom: 4 }}>
              <span
                style={{
                  background: "rgba(255,255,255,0.08)",
                  color: SOFT_VIOLET,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10.5,
                  padding: "1px 6px",
                  borderRadius: 4,
                  marginRight: 5,
                }}
              >
                {e.time}
              </span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11.5, color: "#FFFFFF" }}>
                {e.title}
              </span>
            </div>
          ))}
          <div style={sectionLabel}>TO-DO</div>
          {todoItems.map((t) => (
            <div key={t} className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 3,
                  border: `1.5px solid ${SOFT_VIOLET}`,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11.5, color: "#FFFFFF" }}>
                {t}
              </span>
            </div>
          ))}
          <div className="flex flex-col gap-1 mt-3">
            {["📸 Outlook screenshot", "🔗 Google Calendar", "📝 Type it in"].map((s) => (
              <span
                key={s}
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 9.5,
                  color: SOFT_VIOLET,
                  opacity: 0.67,
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 6,
                  padding: "3px 5px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* MY KIN tab */}
        <div className="welcome-fade" style={{ ...tabCardStyle, animationDelay: "380ms" }}>
          <span style={badgeStyle}>MY KIN</span>
          <div className="flex flex-col gap-2 mt-3">
            {myKinRows.map((r) => (
              <div key={r.label} className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.06)",
                    fontSize: 13,
                  }}
                >
                  {r.icon}
                </div>
                <span
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#FFFFFF",
                  }}
                >
                  {r.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StepShell>
  );
};

// ─── Card 5 — Tasks + Brain Dump (added Aug 4, 2026) ───
//
// The rung/connector markup below is a deliberate copy of Step3's timeline,
// not an extraction. Step3 is inside the frozen zone (CLAUDE.md, May 3 8→6
// compression), so pulling a shared component out of it would mean editing a
// card that already ships. Duplication keeps the blast radius at zero.
const StepTasks = () => {
  const steps = [
    { icon: "🧠", label: "Dump everything rattling around — messy is fine, no lists required", highlight: false },
    { icon: "✨", label: "Kin turns it into real tasks", highlight: false },
    { icon: "⚡", label: "Tap one. Done.", highlight: true },
  ];
  return (
    <StepShell
      headlinePre="Get it out of"
      headlineAccent="your head."
      subline="Dump it messy. Kin sorts it into actual tasks. Tap one and it's done."
    >
      <div className="flex flex-col items-stretch">
        {steps.map((s, i) => (
          <div key={i}>
            <div
              className="welcome-fade flex items-center gap-3"
              style={{
                animationName: "welcomeFadeRight",
                animationDelay: `${260 + i * 100}ms`,
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: s.highlight
                    ? `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`
                    : "rgba(255,255,255,0.07)",
                  boxShadow: s.highlight ? "0 6px 22px rgba(80,80,200,0.4)" : "none",
                  fontSize: 18,
                  color: s.highlight ? "#FFFFFF" : undefined,
                }}
              >
                {s.icon}
              </div>
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: s.highlight ? "#FFFFFF" : SOFT_VIOLET,
                  fontWeight: s.highlight ? 600 : 400,
                  textAlign: "left",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex" style={{ paddingLeft: 19 }}>
                <div
                  style={{
                    width: 1.5,
                    height: 16,
                    background: `linear-gradient(to bottom, rgba(200,200,244,0.2), transparent)`,
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <p
        className="welcome-fade text-center"
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontStyle: "italic",
          fontSize: 11.5,
          color: SOFT_VIOLET,
          opacity: 0.53,
          lineHeight: 1.5,
          marginTop: 14,
          animationDelay: "560ms",
        }}
      >
        It all lives in your Tasks tab — the ⚡ on the far left. Some things Kin just handles on her own.
      </p>
    </StepShell>
  );
};

// ─── Step 5 ───
const Step5 = () => {
  const mocks = [
    { user: "Can you track my reno budget?", kin: "I can! Want me to open the Budget Tracker? It's in your Tools tab." },
    { user: "I need to plan my week.", kin: "Your Today tab is perfect for that. Want me to pull in your Google Calendar too?" },
    { user: "Save this meal plan.", kin: "Saved to My Kin. You can find it anytime under Meal Plans." },
  ];
  return (
    <StepShell
      headlinePre="Kin knows where"
      headlineAccent="things live."
      subline="Ask Kin anything. If there's a better tool for it, Kin will point you there."
    >
      <div className="flex flex-col gap-2">
        {mocks.map((m, i) => (
          <div
            key={i}
            className="welcome-fade flex flex-col gap-1.5"
            style={{
              background: "rgba(255,255,255,0.04)",
              borderRadius: 14,
              padding: "10px 12px",
              animationDelay: `${260 + i * 120}ms`,
            }}
          >
            <div className="flex justify-end">
              <div
                style={{
                  background: `linear-gradient(135deg, ${PERIWINKLE}, ${CHARCOAL})`,
                  color: "#FFFFFF",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  padding: "7px 11px",
                  borderRadius: 12,
                  maxWidth: "85%",
                }}
              >
                {m.user}
              </div>
            </div>
            <div className="flex items-start gap-1.5">
              <KAvatar size={20} />
              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  color: SOFT_VIOLET,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  padding: "7px 11px",
                  borderRadius: 12,
                  maxWidth: "85%",
                  textAlign: "left",
                }}
              >
                {m.kin}
              </div>
            </div>
          </div>
        ))}
      </div>
    </StepShell>
  );
};

// ─── Card 7 — Free and Pro (added Aug 4, 2026) ───
//
// Two-column structure follows Step4's tab-mockup layout (same tabCardStyle
// shape, same items-stretch row, same minWidth: 0 so neither column can push
// the card wider than 390). Copy is Kasandra's verbatim — every number here is
// checked against enforcement (DAILY_MESSAGE_LIMIT = 15 lives in
// supabase/functions/kin-chat/messageCapLogic.ts). Do not round, add to, or
// re-word these lines; no price, no button, no link belongs on this card.
const StepFreePro = () => {
  const colCardStyle = {
    flex: 1,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 14,
    padding: "9px 10px",
    textAlign: "left" as const,
    minWidth: 0,
  };
  const badgeBase = {
    fontFamily: "'Plus Jakarta Sans', sans-serif" as const,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.5px",
    padding: "2px 7px",
    borderRadius: 6,
    color: "#FFFFFF",
    display: "inline-block",
  };
  const freeItems = [
    { icon: "✓", text: "15 chats a day — meal plans and game plans count too" },
    { icon: "✓", text: "Your last 5 conversations, always there" },
    { icon: "✓", text: "Snap-to-cart, Today, Tasks, Tools, and My Kin" },
    { icon: "✓", text: "Brain Dump, as much as you want" },
  ];
  const moreItems = [
    { icon: "⚡", text: "All 20 of Kin's helpers, running on schedule" },
    { icon: "💬", text: "Unlimited chats" },
    { icon: "🎙️", text: "Talk to her instead of typing" },
    { icon: "📧", text: "Forward her an email, she sorts it out" },
    { icon: "🗂️", text: "50 conversations saved" },
  ];
  const renderRow = (icon: string, text: string, tinted: boolean) => (
    <div key={text} className="flex items-start gap-1.5" style={{ marginBottom: 5 }}>
      <span
        className="flex-shrink-0"
        style={{
          fontSize: tinted ? 11 : 11.5,
          lineHeight: 1.4,
          color: tinted ? ELEC_LAV : undefined,
          fontWeight: tinted ? 700 : 400,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 11,
          color: "#FFFFFF",
          lineHeight: 1.35,
          minWidth: 0,
        }}
      >
        {text}
      </span>
    </div>
  );
  return (
    <StepShell
      headlinePre="What you get,"
      headlineAccent="free."
      subline="No trial, no countdown. This part is just yours."
    >
      <div className="flex flex-row gap-2 items-stretch">
        {/* FREE column */}
        <div className="welcome-fade" style={{ ...colCardStyle, animationDelay: "260ms" }}>
          <span
            style={{
              ...badgeBase,
              background: "rgba(255,255,255,0.1)",
              color: SOFT_VIOLET,
              marginBottom: 8,
            }}
          >
            Free
          </span>
          {/* -5 bottom cancels the trailing row's marginBottom so the column
              doesn't carry a dead 5px; this card is the tallest in the tour. */}
          <div style={{ marginTop: 6, marginBottom: -5 }}>
            {freeItems.map((f) => renderRow(f.icon, f.text, true))}
          </div>
        </div>

        {/* PRO column */}
        <div className="welcome-fade" style={{ ...colCardStyle, animationDelay: "380ms" }}>
          <span
            style={{
              ...badgeBase,
              background: `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`,
              marginBottom: 8,
            }}
          >
            When you want more
          </span>
          <div style={{ marginTop: 6, marginBottom: -5 }}>
            {moreItems.map((m) => renderRow(m.icon, m.text, false))}
          </div>
        </div>
      </div>
      <p
        className="welcome-fade text-center"
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontStyle: "italic",
          fontSize: 11.5,
          color: SOFT_VIOLET,
          opacity: 0.53,
          lineHeight: 1.4,
          marginTop: 10,
          animationDelay: "500ms",
        }}
      >
        Pro's there when you want her working in the background. No rush.
      </p>
    </StepShell>
  );
};

// ─── Step 6 ───
const Step6 = () => {
  const buttons = [
    { icon: "👍", label: "Great response", desc: "Tell Kin it nailed it", gradient: false },
    { icon: "👎", label: "Missed the mark", desc: "Help Kin improve", gradient: false },
    { icon: "💡", label: "I have an idea", desc: "Suggest what Kin should do next", gradient: true },
  ];
  return (
    <StepShell
      headlinePre="You're learning AI."
      headlineAccent="And shaping Kin."
      subline="Kin drops tips as you go. Your feedback shapes what gets built next."
    >
      <div
        className="welcome-fade text-left"
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(80,80,200,0.15)",
          borderLeft: `3px solid ${ELEC_LAV}`,
          borderRadius: "4px 12px 12px 4px",
          padding: "10px 14px",
          fontFamily: "'Instrument Serif', serif",
          fontStyle: "italic",
          fontSize: 14.5,
          color: SOFT_VIOLET,
          animationDelay: "200ms",
          marginBottom: 18,
        }}
      >
        “That's called a role prompt, by the way. Super handy for next time.”
      </div>

      <div
        className="welcome-fade flex items-start gap-2 text-left"
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "14px 14px 14px 4px",
          padding: "10px 12px",
          animationDelay: "320ms",
          marginBottom: 18,
        }}
      >
        <KAvatar size={22} />
        <div className="flex-1">
          <div
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5px",
              color: ELEC_LAV,
              marginBottom: 3,
            }}
          >
            Kin
          </div>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: SOFT_VIOLET,
              lineHeight: 1.45,
            }}
          >
            Here's your 5-day meal plan using what's in your fridge. Grocery list is at the bottom for everything you're missing.
          </p>
        </div>
      </div>

      <div className="flex justify-center gap-3">
        {buttons.map((b, i) => (
          <div
            key={b.label}
            className="welcome-fade flex flex-col items-center"
            style={{
              animationName: "welcomeScaleIn",
              animationDelay: `${440 + i * 100}ms`,
              maxWidth: 90,
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                background: b.gradient
                  ? `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`
                  : "rgba(255,255,255,0.07)",
                boxShadow: b.gradient ? "0 4px 16px rgba(80,80,200,0.25)" : "none",
                fontSize: 20,
                marginBottom: 6,
              }}
            >
              {b.icon}
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                color: "#FFFFFF",
                textAlign: "center",
              }}
            >
              {b.label}
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10.5,
                color: SOFT_VIOLET,
                opacity: 0.6,
                textAlign: "center",
                lineHeight: 1.3,
                marginTop: 2,
              }}
            >
              {b.desc}
            </div>
          </div>
        ))}
      </div>
    </StepShell>
  );
};

// Display order. This array is the ONLY source of truth for how many steps the
// tour has — the counter, the dots, the bounds check and the finish trigger all
// read STEPS.length. There is deliberately no TOTAL_STEPS constant to keep in
// sync (removed Aug 4, 2026: it sat 660 lines from this array with no
// cross-check, so a mismatch either hid a step or crashed on STEPS[step]).
//
// Component names still read Step1…Step6 from the original six-card tour, so
// they no longer match card position. Positions are what ships:
//   1 Step1       Welcome
//   2 Step2       Built around your life
//   3 Step3       Snap your fridge
//   4 Step4       Your whole life, one place
//   5 StepTasks   Tasks + Brain Dump          (new)
//   6 Step5       Kin knows where things live
//   7 StepFreePro Free and Pro                (new)
//   8 Step6       You're learning AI          (last on purpose — ends warm)
const STEPS = [Step1, Step2, Step3, Step4, StepTasks, Step5, StepFreePro, Step6];

const WelcomeOnboarding = ({ onDismiss }: WelcomeOnboardingProps) => {
  useStatusBar({ color: '1C1A3E', style: 'light' });
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [visible, setVisible] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const goTo = (next: number) => {
    if (transitioning || next === step || next < 0 || next >= STEPS.length) return;
    setTransitioning(true);
    setTimeout(() => {
      setStep(next);
      setTransitioning(false);
    }, 280);
  };

  const finish = async () => {
    if (user) {
      await supabase
        .from("user_preferences")
        .upsert(
          {
            user_id: user.id,
            onboarding_complete: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );
    }
    setVisible(false);
    setTimeout(() => onDismiss(), 320);
  };

  const handleNext = () => {
    if (step === STEPS.length - 1) finish();
    else goTo(step + 1);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goTo(step + 1);
      else goTo(step - 1);
    }
    touchStartX.current = null;
  };

  const StepComp = STEPS[step];
  const isFinal = step === STEPS.length - 1;

  return (
    <>
      <style>{`
        @keyframes welcomeFadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes welcomeFadeLeft {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes welcomeFadeRight {
          from { opacity: 0; transform: translateX(16px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes welcomeScaleIn {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes welcomeFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes welcomeShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes welcomeCardIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .welcome-fade {
          opacity: 0;
          animation-name: welcomeFadeUp;
          animation-duration: 0.45s;
          animation-fill-mode: forwards;
          animation-timing-function: ease-out;
        }
        .welcome-float {
          animation: welcomeFloat 3s ease-in-out infinite, welcomeFadeUp 0.45s ease-out forwards;
        }
        .welcome-shimmer-overlay {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
          background-size: 200% 100%;
          animation: welcomeShimmer 3s infinite linear;
          pointer-events: none;
        }
      `}</style>
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{
          zIndex: 9999,
          background: "rgba(28, 26, 62, 0.88)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.32s ease-out",
          padding: "0 16px",
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="relative flex flex-col"
          style={{
            width: "100%",
            maxWidth: 390,
            maxHeight: "94vh",
            borderRadius: 26,
            background: `linear-gradient(168deg, ${CHARCOAL} 0%, #1a1840 45%, #15133a 100%)`,
            border: "1px solid rgba(80, 80, 200, 0.1)",
            boxShadow: "0 28px 70px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
            overflow: "hidden",
            animation: visible ? "welcomeCardIn 0.45s ease-out" : "none",
          }}
        >
          {/* dot grid */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              pointerEvents: "none",
            }}
          />
          {/* glow orbs removed for cleaner card visuals */}

          {/* Header */}
          <div className="relative flex items-center justify-between" style={{ padding: "16px 20px 0" }}>
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10,
                color: SOFT_VIOLET,
                opacity: 0.33,
              }}
            >
              {step + 1} / {STEPS.length}
            </span>
            <button
              onClick={finish}
              style={{
                background: "transparent",
                border: "none",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 15,
                color: "#C8C8F4",
                opacity: 1,
                textDecoration: "underline",
                textDecorationThickness: 1,
                cursor: "pointer",
                padding: "12px 16px",
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Skip
            </button>
          </div>

          {/* Body */}
          <div
            className="relative flex-1 overflow-y-auto"
            style={{
              padding: "18px 22px 8px",
              opacity: transitioning ? 0 : 1,
              transform: transitioning ? "translateY(10px)" : "translateY(0)",
              transition: "opacity 0.28s ease-out, transform 0.28s ease-out",
            }}
            key={step}
          >
            <StepComp />
          </div>

          {/* Footer */}
          <div className="relative" style={{ padding: "12px 22px 18px" }}>
            <div className="flex justify-center items-center gap-1.5 mb-3">
              {Array.from({ length: STEPS.length }).map((_, i) => {
                const active = i === step;
                return (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    aria-label={`Go to step ${i + 1}`}
                    style={{
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      background: "transparent",
                      width: 28,
                      height: 28,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        width: active ? 22 : 7,
                        height: 7,
                        borderRadius: 999,
                        background: active
                          ? `linear-gradient(90deg, ${PERIWINKLE}, ${ELEC_LAV})`
                          : "rgba(255,255,255,0.15)",
                        transition: "width 0.3s ease, background 0.3s ease",
                      }}
                    />
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  onClick={() => goTo(step - 1)}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "14px 20px",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    color: SOFT_VIOLET,
                    opacity: 0.6,
                  }}
                >
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="relative overflow-hidden"
                style={{
                  flex: 1,
                  height: 52,
                  borderRadius: 14,
                  border: "none",
                  cursor: "pointer",
                  background: isFinal
                    ? `linear-gradient(90deg, ${PERIWINKLE}, ${ELEC_LAV})`
                    : "rgba(255,255,255,0.07)",
                  color: isFinal ? "#FFFFFF" : SOFT_VIOLET,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  boxShadow: isFinal ? "0 8px 28px rgba(80,80,200,0.25)" : "none",
                }}
              >
                {isFinal && <span className="welcome-shimmer-overlay" />}
                <span style={{ position: "relative" }}>{isFinal ? "Let's do this" : "Next"}</span>
              </button>
            </div>
            <div
              className="text-center mt-3"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 9,
                color: SOFT_VIOLET,
                opacity: 0.5,
                letterSpacing: "1.5px",
              }}
            >
              KINYOUDOITALL.COM
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WelcomeOnboarding;
